import { useState } from 'react';
import { useGT } from 'gt-react';
import { AGENT_EVAL_JUDGE_MODELS } from '@erp/rpc';
import { Input } from 'src/components/ui/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';

const DEFAULT_KEY = '__default__';
const CUSTOM_KEY = '__custom__';
const DEFAULT_MODEL = 'anthropic/claude-sonnet-5';
const PROVIDERS = ['Anthropic', 'OpenAI', 'Google'] as const;
type Provider = (typeof PROVIDERS)[number];
type JudgeModel = (typeof AGENT_EVAL_JUDGE_MODELS)[number];

const MODELS_BY_PROVIDER: Record<Provider, JudgeModel[]> = {
  Anthropic: [],
  OpenAI: [],
  Google: [],
};
for (const model of AGENT_EVAL_JUDGE_MODELS) {
  if (model.value !== DEFAULT_MODEL) {
    MODELS_BY_PROVIDER[model.provider].push(model);
  }
}

/**
 * Judge/grader model picker (AI-340). A dropdown of curated presets plus a "Custom…" escape that
 * reveals a free-text input — so the friendly common cases are one click, but any litellm model id
 * still works. An empty value means "inherit the ai-service default grader".
 */
export function JudgeModelSelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const gt = useGT();
  const providerLabels: Record<Provider, string> = {
    Anthropic: gt('Anthropic', { context: 'judge model provider group' }),
    OpenAI: gt('OpenAI', { context: 'judge model provider group' }),
    Google: gt('Google', { context: 'judge model provider group' }),
  };
  const modelLabels: Record<string, string> = {
    'anthropic/claude-opus-5': gt('Claude Opus 5 — Most Thorough', {
      context: 'judge model picker option',
    }),
    'anthropic/claude-haiku-5-5': gt('Claude Haiku 5.5 — Lowest Cost', {
      context: 'judge model picker option',
    }),
    'openai/gpt-5.6-terra': gt('GPT-5.6 Terra — Balanced Independent Judge', {
      context: 'judge model picker option',
    }),
    'openai/gpt-5.6-sol': gt('GPT-5.6 Sol — Deepest Independent Review', {
      context: 'judge model picker option',
    }),
    'openai/gpt-5.6-luna': gt('GPT-5.6 Luna — Fast and Economical', {
      context: 'judge model picker option',
    }),
    'google/gemini-3.8-flash': gt('Gemini 3.8 Flash — Multimodal and Fast', {
      context: 'judge model picker option',
    }),
  };
  const isPreset = AGENT_EVAL_JUDGE_MODELS.some((m) => m.value === value);
  // Custom mode is sticky once entered; seeded true when the saved value isn't a known preset.
  const [custom, setCustom] = useState(value !== '' && !isPreset);

  const selectValue = custom
    ? CUSTOM_KEY
    : value === '' || value === DEFAULT_MODEL
      ? DEFAULT_KEY
      : value;

  return (
    <div className="flex flex-col gap-2">
      <Select
        value={selectValue}
        onValueChange={(v) => {
          if (v === CUSTOM_KEY) {
            setCustom(true);
            return;
          }
          setCustom(false);
          onChange(v === DEFAULT_KEY ? '' : v);
        }}
      >
        <SelectTrigger className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {PROVIDERS.map((provider) => (
            <SelectGroup key={provider}>
              <SelectLabel>{providerLabels[provider]}</SelectLabel>
              {provider === 'Anthropic' && (
                <SelectItem value={DEFAULT_KEY}>
                  {gt('Claude Sonnet 5 — Recommended', {
                    context:
                      'judge model picker: inherit the recommended default grader',
                  })}
                </SelectItem>
              )}
              {MODELS_BY_PROVIDER[provider].map((model) => (
                <SelectItem key={model.value} value={model.value}>
                  {modelLabels[model.value] ?? model.label}
                </SelectItem>
              ))}
            </SelectGroup>
          ))}
          <SelectGroup>
            <SelectLabel>
              {gt('Advanced', { context: 'judge model picker group' })}
            </SelectLabel>
            <SelectItem value={CUSTOM_KEY}>
              {gt('Use a Custom Model…', {
                context: 'judge model picker: enter a custom model id',
              })}
            </SelectItem>
          </SelectGroup>
        </SelectContent>
      </Select>
      {custom && (
        <Input
          placeholder={gt('openai/gpt-5.6-terra', {
            context: 'example model id shown in the custom judge-model input',
          })}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </div>
  );
}
